// Which colour scheme the pane should show, decided from what the host tells us.
//
// Pure on purpose: the Office API surface this reads is uneven (isDarkTheme is not
// supported in Outlook; the whole object is missing on some platforms) and the
// decision is worth testing without an Office global. The hook in
// hooks/useOfficeThemeSync does the reading and hands the result here.

export type PaneMode = "light" | "dark";

/** The subset of Office.OfficeTheme this decision needs, typed loosely because the host may omit any of it. */
export interface OfficeThemeReading {
  isDarkTheme?: unknown;
  bodyBackgroundColor?: unknown;
}

/**
 * WCAG relative luminance of a hex colour, 0 (black) to 1 (white), or null when the
 * string is not a colour. Accepts `#rgb`, `#rrggbb` and `#rrggbbaa`, with or without
 * the hash.
 */
export const relativeLuminance = (value: unknown): number | null => {
  if (typeof value !== "string") return null;
  let hex = value.trim().replace(/^#/, "");
  if (hex.length === 3) hex = hex.replace(/./g, (c) => c + c);
  if (hex.length === 8) hex = hex.slice(0, 6);
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null;

  const channel = (offset: number) => {
    const c = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
};

/**
 * Resolve the pane's colour scheme.
 *
 * The body background colour is preferred over `isDarkTheme` because it is reported
 * everywhere the theme object exists at all, Outlook included, where the flag is not
 * supported and cannot be told apart from "light". The flag is the fallback when the
 * colour is missing or unparseable; the operating system preference is the fallback
 * when Office says nothing.
 */
export const resolvePaneMode = (
  reading: OfficeThemeReading | null | undefined,
  prefersDark: boolean
): PaneMode => {
  const luminance = relativeLuminance(reading?.bodyBackgroundColor);
  if (luminance !== null) return luminance < 0.5 ? "dark" : "light";
  if (typeof reading?.isDarkTheme === "boolean") return reading.isDarkTheme ? "dark" : "light";
  return prefersDark ? "dark" : "light";
};

/**
 * What the user asked for in the header menu. "system" is the default and means what it
 * always meant: follow Office, then the operating system. "light" and "dark" are an
 * explicit override, and the only thing that is ever remembered.
 */
export type ColorPreference = "system" | "light" | "dark";

export const COLOR_PREFERENCES: readonly ColorPreference[] = ["system", "light", "dark"];

/**
 * Read a stored preference back. Anything unrecognised -- nothing stored, a value from
 * some future build, a string someone typed into devtools -- is "system", so a bad
 * value can only ever fall back to following Office, never pin the pane to a scheme.
 */
export const parseColorPreference = (raw: unknown): ColorPreference =>
  raw === "light" || raw === "dark" ? raw : "system";

/** The scheme to show: the user's explicit choice if they made one, otherwise Office's. */
export const resolvePaneModeWithPreference = (
  preference: ColorPreference,
  reading: OfficeThemeReading | null | undefined,
  prefersDark: boolean
): PaneMode => (preference === "system" ? resolvePaneMode(reading, prefersDark) : preference);
